import { message } from 'antd';
import { useCallback } from 'react';
import { updateAdminProductPrice } from '../../services';
import type { UseAdminProductCatalogArgs } from '../interfaces';
import { updateProductPriceInList } from './adminProductForm.utils';
import { getApiErrorMessage } from '../../utils/apiError';

type UseAdminProductPriceActionsArgs = Pick<
  UseAdminProductCatalogArgs,
  | 'draftPriceById'
  | 'savingPriceId'
  | 'setBaseProducts'
  | 'setDraftPriceById'
  | 'setProducts'
  | 'setSavingPriceId'
>;

export const useAdminProductPriceActions = ({
  draftPriceById,
  savingPriceId,
  setBaseProducts,
  setDraftPriceById,
  setProducts,
  setSavingPriceId,
}: UseAdminProductPriceActionsArgs) => {
  const handlePriceChange = useCallback(async (materialPriceId: string, price: number | null) => {
    const nextPrice = Math.max(0, Number(price || 0));

    if (nextPrice <= 0) {
      message.error('Material price must be greater than 0.');
      return;
    }

    try {
      setSavingPriceId(materialPriceId);
      await updateAdminProductPrice(materialPriceId, nextPrice);
      setBaseProducts((prev) => updateProductPriceInList(prev, materialPriceId, nextPrice));
      setProducts((prev) => updateProductPriceInList(prev, materialPriceId, nextPrice));
      message.success('Price updated.');
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to update price.'));
    } finally {
      setSavingPriceId(null);
    }
  }, [setBaseProducts, setProducts, setSavingPriceId]);

  const handleDraftPriceInput = useCallback((materialPriceId: string, value: number | null) => {
    setDraftPriceById((prev) => ({
      ...prev,
      [materialPriceId]: Math.max(0, Number(value || 0)),
    }));
  }, [setDraftPriceById]);

  const commitDraftPrice = useCallback((materialId: string, originalPrice: number) => {
    const draftPrice = draftPriceById[materialId];
    const nextPrice = typeof draftPrice === 'number' ? draftPrice : originalPrice;

    if (nextPrice === originalPrice || savingPriceId === materialId) {
      return;
    }

    void handlePriceChange(materialId, nextPrice);
  }, [draftPriceById, handlePriceChange, savingPriceId]);

  return { commitDraftPrice, handleDraftPriceInput };
};
